import React, { useState, useEffect } from "react";
import { AuthUserContext } from "../Session";
import { withFirebase } from "../Firebase";
import { withRouter } from "react-router-dom";
import { compose } from "recompose";
import {
  Typography,
  Grid,
  CardContent,
  CardActions,
  Divider,
  Button,
  Box,
  Container,
  Fade,
  Grow,
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import CurrencyFormat from "react-currency-format";
import DashboardGame from "./DashboardGame";
import { useError } from "../ErrorProvider";
import MonthSummary from "./MonthSummary";
import playSound from "../../audio/sonicID.m4a";
import { PageHeader, AppCard, EmptyState, CardSkeleton } from "../UI";
import { brand } from "../StyledComponents/theme";

const useStyles = makeStyles((theme) => ({
  banner: {
    background: brand.navyGradient,
    color: "white",
    padding: theme.spacing(2.5, 3),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2.5),
    boxShadow: brand.cardShadow,
  },
  bannerQuarterly: {
    background: brand.greenGradient,
  },
  cardTitle: {
    textAlign: "center",
  },
  cardSubtitle: {
    textAlign: "center",
    marginTop: theme.spacing(1),
  },
  savingsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 0),
  },
  savingsLabel: {
    fontSize: 13,
    color: brand.textMedium,
  },
  savingsValue: {
    fontSize: 14,
    fontWeight: 600,
  },
  savingsHighlight: {
    color: brand.green,
    fontSize: 16,
  },
  cardFooter: {
    padding: theme.spacing(2, 0),
    flexBasis: "100%",
    textAlign: "center",
  },
}));

const WhiteButton = withStyles(() => ({
  root: {
    color: "white",
    borderColor: "white",
    "&:hover": {
      borderColor: "white",
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
  },
}))(Button);

const audio = new Audio(playSound);

const DashboardHome = (props) => (
  <AuthUserContext.Consumer>
    {(authUser) => <DashboardHomeAuth authUser={authUser} {...props} />}
  </AuthUserContext.Consumer>
);

const DashboardHomeAuth = ({ authUser, firebase, history }) => {
  const classes = useStyles();

  const getMonthName = (month) => {
    return new Intl.DateTimeFormat("en-US", { month: "long" }).format(
      new Date(2000, month, 1)
    );
  };

  const getCurrentQuarterStart = () => {
    const month = new Date().getMonth();
    return month - (month % 3);
  };

  // mirrors the backend rule: the drawing on the first Monday of a quarter
  // (Jan/Apr/Jul/Oct) is the quarterly drawing, replacing that week's weekly one
  const isQuarterlyDrawingWeek = () => {
    const date = new Date();
    const lastMonday = new Date(date);
    lastMonday.setDate(date.getDate() - ((date.getDay() + 6) % 7));

    return (
      [0, 3, 6, 9].includes(lastMonday.getMonth()) && lastMonday.getDate() <= 7
    );
  };

  const playGame = () => {
    audio.play();
    setView("game");
    setWeeklyGameEligible(false);
  };

  const gamePlayed = () => {
    setLoading(true);
    setView("dashboard");
    setWeeklyGameEligible(false);
    firebase
      .userPlayedGame({})
      .catch((err) => showError(err))
      .finally((d) => {
        setLoading(false);
      });
  };

  const { showError } = useError();
  const [view, setView] = useState("dashboard");
  const [user, setUser] = useState({
    name: "",
    weeklySavingsAmount: 0,
    yearlySavingsTotal: 0,
    lifetimeSavingsTotal: 0,
    MonthOne: getMonthName(getCurrentQuarterStart()),
    MonthTwo: getMonthName(getCurrentQuarterStart() + 1),
    MonthThree: getMonthName(getCurrentQuarterStart() + 2),
  });
  const [loading, setLoading] = useState(true);
  const [weeklyGameEligible, setWeeklyGameEligible] = useState(false);
  const [weeklyWinner, setWeeklyWinner] = useState(false);
  const [quarterlyWinner, setQuarterlyWinner] = useState(false);

  const loadData = () => {
    if (!!authUser) {
      setLoading(true);
      Promise.allSettled([
        firebase
          .user(authUser.uid)
          .then((val) => {
            let userData = val.data();

            setWeeklyGameEligible(userData.weeklyGameStatus === "eligible");

            setUser((prevState) => ({
              ...prevState,
              name: userData.firstName || userData.username || "",
              weeklySavingsAmount: userData.weeklySavingsAmount,
              yearlySavingsTotal: userData.yearlySavingsTotal,
              lifetimeSavingsTotal: userData.lifetimeSavingsTotal,
              MonthOne: getMonthName(getCurrentQuarterStart()),
              MonthTwo: getMonthName(getCurrentQuarterStart() + 1),
              MonthThree: getMonthName(getCurrentQuarterStart() + 2),
              QuarterStart: getCurrentQuarterStart(),
            }));
          })
          .catch((err) => {
            showError(err);
          }),
        firebase
          .isUserWinner()
          .then((d) => {
            setWeeklyWinner(d.data.weeklyWinner);
            setQuarterlyWinner(d.data.quarterlyWinner);
          })
          .catch((err) => showError(err)),
      ]).finally(() => setLoading(false));
    }
  };

  useEffect(loadData, [authUser]);

  const isQuarterly = isQuarterlyDrawingWeek() || quarterlyWinner;
  const isNewSaver =
    !loading &&
    !user.weeklySavingsAmount &&
    !user.yearlySavingsTotal &&
    !user.lifetimeSavingsTotal;

  const savingsRow = (label, value, highlight) => (
    <div className={classes.savingsRow}>
      <span className={classes.savingsLabel}>{label}</span>
      <span
        className={`${classes.savingsValue} ${
          highlight ? classes.savingsHighlight : ""
        }`}
      >
        <CurrencyFormat
          value={value}
          displayType={"text"}
          thousandSeparator={true}
          prefix={"$"}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      </span>
    </div>
  );

  return (
    <div>
      <PageHeader
        title={`Hello ${user.name}`}
        quote="Save like a champion today"
      />
      <Container>
        {process.env.NODE_ENV === "development" && !weeklyGameEligible && view === "dashboard" && (
          <Box mb={1} textAlign="right">
            <Button size="small" variant="outlined" onClick={() => { setWeeklyGameEligible(true); }}>
              Dev: Preview Game
            </Button>
          </Box>
        )}
        <Grow in={weeklyGameEligible} unmountOnExit>
          <div
            className={`${classes.banner} ${
              isQuarterly ? classes.bannerQuarterly : ""
            }`}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={10}>
                <Typography variant="h4" style={{ color: "white" }}>
                  Whooo Hooo, Let's Play the{" "}
                  {isQuarterly ? "Quarterly" : "Weekly"} Sweepstakes!
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2} style={{ textAlign: "right" }}>
                <WhiteButton variant="outlined" onClick={playGame}>
                  Play
                </WhiteButton>
              </Grid>
              <Grid item xs={12} align="left">
                <Typography variant="body1" style={{ color: "white" }}>
                  You've earned it.
                </Typography>
              </Grid>
            </Grid>
          </div>
        </Grow>
        {view === "dashboard" && (
          <Fade in timeout={400}>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="stretch"
              spacing={3}
            >
              <Grid item xs={12} sm={6}>
                {loading ? (
                  <CardSkeleton rows={4} />
                ) : (
                  <AppCard style={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="h4" className={classes.cardTitle}>
                        Weekly Squirrel Sweepstakes
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.cardSubtitle}
                      >
                        Save every week to enter the sweepstakes!
                      </Typography>
                      <Box mt={3}>
                        {isNewSaver ? (
                          <EmptyState
                            title="Your savings journey starts soon!"
                            message="Once your HR admin sets your weekly savings amount, your progress will show up here."
                          />
                        ) : (
                          <>
                            {savingsRow(
                              "Savings Per Week",
                              user.weeklySavingsAmount
                            )}
                            <Divider />
                            {savingsRow(
                              "Savings So Far This Year",
                              user.yearlySavingsTotal
                            )}
                            <Divider />
                            {savingsRow(
                              "Total Savings Since Joining",
                              user.lifetimeSavingsTotal,
                              true
                            )}
                          </>
                        )}
                      </Box>
                    </CardContent>
                    <Divider orientation="horizontal" variant="fullWidth" />
                    <CardActions>
                      <Box className={classes.cardFooter}>
                        <Typography variant="body2">
                          Weekly sweepstakes held every Monday!
                        </Typography>
                      </Box>
                    </CardActions>
                  </AppCard>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {loading ? (
                  <CardSkeleton rows={4} />
                ) : (
                  <AppCard style={{ height: "100%" }}>
                    <CardContent>
                      <Typography variant="h4" className={classes.cardTitle}>
                        Quarterly Squirrel Sweepstakes
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.cardSubtitle}
                      >
                        Keep saving to reach the quarterly sweepstakes!
                      </Typography>
                      <Box mt={3}>
                        <Grid container spacing={2}>
                          <Grid item lg={4} xs={12}>
                            <MonthSummary
                              month={user.MonthOne}
                              numericMonth={user.QuarterStart}
                            />
                          </Grid>
                          <Grid item lg={4} xs={12}>
                            <MonthSummary
                              month={user.MonthTwo}
                              numericMonth={user.QuarterStart + 1}
                            />
                          </Grid>
                          <Grid item lg={4} xs={12}>
                            <MonthSummary
                              month={user.MonthThree}
                              numericMonth={user.QuarterStart + 2}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                    <Divider orientation="horizontal" variant="fullWidth" />
                    <CardActions>
                      <Box className={classes.cardFooter}>
                        <Typography variant="body2">
                          $100 Quarterly Sweepstakes held on the last week of
                          each quarter!
                        </Typography>
                      </Box>
                    </CardActions>
                  </AppCard>
                )}
              </Grid>
            </Grid>
          </Fade>
        )}
        {view === "game" && (
          <Fade in timeout={400}>
            <div>
              <DashboardGame
                isWinner={weeklyWinner || quarterlyWinner}
                callback={gamePlayed}
              />
            </div>
          </Fade>
        )}
      </Container>
    </div>
  );
};

export default compose(withFirebase, withRouter)(DashboardHome);
